import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useCurrentUser } from 'api/authentication/hooks';
import { UserRole } from 'api/users/requests';

type Permissions = {
  role: UserRole | null;

  isAdmin: boolean;
  isEditor: boolean;
  isContributor: boolean;

  canViewSettings: boolean;
  canManageUsers: boolean;
  canManageCompany: boolean;
  canManageBilling: boolean;
  canManageEmailDomains: boolean;
  canManageSecurity: boolean;
  canManageIntegrations: boolean;
  canManagePortals: boolean;
  canManageApiKeys: boolean;
  canManageWorkspaces: boolean;

  canViewTravellerProfiles: boolean;
  canViewLists: boolean;
  canViewAiAgents: boolean;
  canFixWithNavigator: boolean;
  canManageJourneyTravellers: boolean;
  canEditJourneyTravellerData: boolean;
};

const DEFAULT_PERMISSIONS: Permissions = {
  role: null,

  isAdmin: false,
  isEditor: false,
  isContributor: false,

  canViewSettings: false,
  canManageUsers: false,
  canManageCompany: false,
  canManageBilling: false,
  canManageEmailDomains: false,
  canManageSecurity: false,
  canManageIntegrations: false,
  canManagePortals: false,
  canManageApiKeys: false,
  canManageWorkspaces: false,

  canViewTravellerProfiles: false,
  canViewLists: false,
  canViewAiAgents: false,
  canFixWithNavigator: false,
  canManageJourneyTravellers: false,
  canEditJourneyTravellerData: false,
};

const PermissionsContext = createContext<Permissions>(DEFAULT_PERMISSIONS);

const getPermissions = (role: UserRole): Permissions => {
  const isAdmin = role === 'admin';
  const isEditor = role === 'editor';
  const isContributor = role === 'contributor';
  const isAdminOrEditor = isAdmin || isEditor;

  return {
    role,

    isAdmin,
    isEditor,
    isContributor,

    canViewSettings: isAdminOrEditor,
    canManageUsers: isAdmin,
    canManageCompany: isAdmin,
    canManageBilling: isAdmin,
    canManageEmailDomains: isAdminOrEditor,
    canManageSecurity: isAdminOrEditor,
    canManageIntegrations: isAdminOrEditor,
    canManagePortals: isAdminOrEditor,
    canManageApiKeys: isAdminOrEditor,
    canManageWorkspaces: isAdminOrEditor,

    canViewTravellerProfiles: isAdminOrEditor,
    canViewLists: isAdminOrEditor,
    canViewAiAgents: isAdminOrEditor,
    canFixWithNavigator: isAdminOrEditor,
    canManageJourneyTravellers: isAdminOrEditor,
    canEditJourneyTravellerData: true,
  };
};

export type PermissionsProviderProps = {
  children: ReactNode;
};

export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const currentUser = useCurrentUser();
  const role = currentUser?.role ?? null;

  const permissions = useMemo(() => {
    if (!role) {
      return DEFAULT_PERMISSIONS;
    }
    return getPermissions(role);
  }, [role]);

  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  return useContext(PermissionsContext);
};

type PermissionKey = keyof Omit<Permissions, 'role'>;

export const PermissionGuard = ({
  permission,
  children,
}: {
  permission: PermissionKey;
  children: ReactNode;
}) => {
  const permissions = usePermissions();
  if (!permissions[permission]) {
    return null;
  }
  return <>{children}</>;
};
